import { useEffect, useMemo, useState } from 'react'
import { type NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useActivePaymentProfile, useGetPaymentProfiles } from 'dashboard-experience/shared/hooks'
import curry from 'lodash.curry'

import { Heimdall } from '~/domains/platform/core/heimdall'
import { Loader } from '~/domains/platform/design-system/loader/loader'
import { readPreviousRoute } from '~/domains/platform/lib/route-history/route-history'
import { CleanPageContainer } from '~/ui/containers'

import { useDeusExMachina } from '../deus-ex-machina/hocs/with-deus-ex-machina'
import { useIsOneStone } from '../deus-ex-machina/hooks/use-is-one-stone'
import { type WithDeusExMachinaProps } from '../deus-ex-machina/types'

const PaymentProfileSelectorPage = dynamic(
  () => import('dashboard-experience/shared/components').then(mod => mod.PaymentProfileSelectorPage),
  {
    loading: () => (
      <CleanPageContainer>
        <Loader />
      </CleanPageContainer>
    )
  }
)

export interface PaymentProfileGateConfig {
  /** Inclui payment profiles inativos na seleção. Default: false (só ativos). */
  includeInactive?: boolean
  /** Rota do botão voltar. Default: /home. */
  backRoute?: string
  /**
   * Força a reseleção de loja mesmo quando já há uma ativa. Default: false.
   * Aceite um objeto com `ignoreFromRoutes` para pular a reseleção quando a navegação
   * veio de uma dessas rotas (ex.: voltar de um cadastro).
   */
  forceReselect?: boolean | { ignoreFromRoutes?: string[] }
}

export type PaymentProfileGateArg = boolean | PaymentProfileGateConfig

function normalizeForceReselect(value: PaymentProfileGateConfig['forceReselect']) {
  if (!value) return { enabled: false, ignoreFromRoutes: [] as string[] }
  if (value === true) return { enabled: true, ignoreFromRoutes: [] as string[] }
  return { enabled: true, ignoreFromRoutes: value.ignoreFromRoutes ?? [] }
}

export const withPaymentProfileGate = curry(function withPaymentProfileGate(
  arg: PaymentProfileGateArg,
  Page: NextPage<WithDeusExMachinaProps>
) {
  const config: PaymentProfileGateConfig = typeof arg === 'boolean' ? {} : arg
  const forceReselect = normalizeForceReselect(config.forceReselect)

  function WrappedPage(props: WithDeusExMachinaProps) {
    const isEnabled = Heimdall.pass(['engineering-cj-core-payment-profile'])
    const isOneStone = useIsOneStone()
    const { deusExMachina } = useDeusExMachina()
    const stoneAccountId = deusExMachina?.entity?.stoneAccount?.id ?? ''
    const { activeProfileId, setActiveProfile } = useActivePaymentProfile()

    const gateActive = isEnabled && isOneStone

    const { data: profiles = [], isLoading } = useGetPaymentProfiles({
      stoneAccountId,
      enabled: gateActive && Boolean(stoneAccountId)
    })

    const eligibleProfiles = useMemo(
      () => (config.includeInactive ? profiles : profiles.filter(profile => profile.enabled)),
      [profiles]
    )
    const onlyProfileId = eligibleProfiles.length === 1 ? eligibleProfiles[0].id : ''
    const isActiveProfileEligible = eligibleProfiles.some(profile => profile.id === activeProfileId)

    const cameFromIgnoredRoute = useMemo(() => {
      if (!forceReselect.enabled || forceReselect.ignoreFromRoutes.length === 0) return false
      const previousRoute = readPreviousRoute()
      if (!previousRoute) return false
      return forceReselect.ignoreFromRoutes.some(route => previousRoute === route || previousRoute.startsWith(route))
    }, [])

    const [reselected, setReselected] = useState(false)

    useEffect(() => {
      if (!gateActive || isLoading) return
      if (onlyProfileId && activeProfileId !== onlyProfileId) setActiveProfile(onlyProfileId)
    }, [gateActive, isLoading, activeProfileId, onlyProfileId, setActiveProfile])

    if (!gateActive) return <Page {...props} />

    if (isLoading) {
      return (
        <CleanPageContainer>
          <Loader />
        </CleanPageContainer>
      )
    }

    // Loja única: auto-selecionada pelo efeito acima, nunca mostra a tela de seleção.
    if (onlyProfileId) {
      if (activeProfileId === onlyProfileId) return <Page {...props} />
      return (
        <CleanPageContainer>
          <Loader />
        </CleanPageContainer>
      )
    }

    const forcing = forceReselect.enabled && !cameFromIgnoredRoute && !reselected
    const mustSelect = forcing || !isActiveProfileEligible

    if (mustSelect) {
      return (
        <PaymentProfileSelectorPage
          includeInactive={config.includeInactive}
          backRoute={config.backRoute}
          onSelect={() => setReselected(true)}
        />
      )
    }

    return <Page {...props} />
  }

  WrappedPage.displayName = `withPaymentProfileGate(${Page.displayName})`

  return WrappedPage
})
